import {
  createRoot,
  createSignal,
  createEffect,
  createMemo,
  Show,
  onCleanup,
} from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/+esm";
import { insert } from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/web/+esm";
import {
  register,
  compose,
  noShadowDOM,
} from "https://cdn.jsdelivr.net/npm/component-register/+esm";

import html from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/html/+esm";
import uniqBy from "https://cdn.jsdelivr.net/npm/lodash.uniqby@4.7.0/+esm";
import StorefrontCart from "https://cdn.jsdelivr.net/npm/@bryt-designs/storefront-cart@2.2.41";
import { getProductContext } from "https://cdn.jsdelivr.net/npm/@brytdesigns/shopify-product-info-components@1.0.6/+esm";

// Utility custom element functions

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner)
    return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (
    next &&
    !next._$owner &&
    !(next.assignedSlot && next.assignedSlot._$owner)
  )
    next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = "";
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(Component) {
  return (props, options) => {
    noShadowDOM();
    const comp = Component(props, options);
    return comp;
  };
}

const customShadowlessElement = (
  tagName,
  props = {},
  Component = (props, options) => {},
  ...rest
) =>
  compose(
    register(tagName, props),
    withNoShadowDOM,
    withSolid,
    ...rest,
  )(Component);

function getDiscount() {
  if (typeof window.DiscountApp === "undefined")
    return console.warn(
      "DiscountApp is not found! Please ensure DiscountApp is properly initialized.",
    );
  return window.DiscountApp.discount;
}

function formatValue(format, value) {
  switch (format) {
    case "currency":
      return Shopify.formatMoney(value);
    case "number":
      return Number(value);
    case "boolean":
      return typeof value === "string"
        ? value.toLowerCase() === "true"
        : Boolean(value);
    case "invert_boolean":
      return typeof value === "string"
        ? value.toLowerCase() === "false"
        : !Boolean(value);
    default:
      return value;
  }
}

function getDiscountedPrice(variantPrice, discount) {
  if (!discount || !discount?.code?.amount) return variantPrice;
  if (discount.code.amount.type === "FREE_SHIPPING") return variantPrice;
  let price = variantPrice;

  if (discount.code.amount.type === "PERCENTAGE") {
    price = price - price * discount.code.amount.value;
  }

  if (discount.code.amount.type === "FIXED") {
    price = price - parseFloat(discount.code.amount.value) * 100;
  }

  return price;
}

function Price(props) {
  return html` <span class=${() => props.class}> ${props.children} </span> `;
}

customShadowlessElement(
  "bundle-variant-price",
  { sellingPlan: 0, actualClass: "", priceClass: "", compareClass: "" },
  (props, { element }) => {
    const discount = getDiscount();

    const productInfo = element.closest("product-info");
    if (!productInfo)
      return console.warn(
        "bundle-variant-subscription-price: No product-info parent element found",
      );
    if (!props.sellingPlan)
      return console.warn(
        "bundle-variant-price: selling-plan attribute required!",
      );

    const [state, _] = getProductContext(productInfo);

    const selectedVariant = createMemo(
      () => state.product.selected_or_first_available_variant,
    );

    const sellingPlanAllocation = createMemo(() => {
      return selectedVariant().selling_plan_allocations.find(
        (allocation) => allocation.selling_plan_id === props.sellingPlan,
      );
    });

    const purchaseOption = createMemo(
      () =>
        StorefrontCart.cart()?.attributes?.find(
          (attr) => attr.key === "_purchaseOption",
        )?.value || null,
    );

    const actualPrice = createMemo(() => {
      const d = discount();
      const variant = selectedVariant();
      const allocation = sellingPlanAllocation();
      const option = purchaseOption();
      if (!variant) return null;
      if (allocation && option === "sub")
        return getDiscountedPrice(allocation.price, d);
      return getDiscountedPrice(variant.price, d);
    });

    const compareAtPrice = createMemo(() => {
      const variant = selectedVariant();
      const allocation = sellingPlanAllocation();
      const option = purchaseOption();
      if (!variant) return null;
      if (allocation && option === "sub")
        return allocation.compare_at_price || 0;
      return variant.compare_at_price || variant.price || 0;
    });

    const formattedActualPrice = () => formatValue("currency", actualPrice()),
      formattedCompareAtPrice = () => formatValue("currency", compareAtPrice()),
      priceClass = () =>
        props.priceClass
          ? `variant-price ${props.priceClass}`
          : "variant-price",
      actualClass = () =>
        props.actualClass
          ? `variant-price__actual ${props.actualClass}`
          : "variant-price__actual",
      compareClass = () =>
        props.compareClass
          ? `variant-price__compare ${props.compareClass}`
          : "variant-price__compare";

    const clonedChildren = () =>
      Array.from(element.children).map((child) => child.cloneNode(true));

    element.replaceChildren(...[]);

    return html`
      <${Show}
        when=${() =>
          typeof actualPrice() === "number" &&
          typeof compareAtPrice() === "number"}
        fallback=${clonedChildren}
      >
        <${Price} class=${priceClass}>
          <${Show} when=${() => actualPrice() < compareAtPrice()}>
            <s class=${compareClass}>${formattedCompareAtPrice}</s>
          <//>
          <span class=${actualClass}>${formattedActualPrice}</span>
        <//>
      <//>
    `;
  },
);

customShadowlessElement(
  "bundle-subscription-price",
  { actualClass: "", priceClass: "", compareClass: "", sectionId: "" },
  (props, { element }) => {
    if (!props.sectionId)
      return console.warn(
        "bundle-subscription-price: section-id attribute required!",
      );

    const section = createMemo(() => document.getElementById(props.sectionId));
    const discount = getDiscount();

    const productInfoElements = createMemo(() =>
      Array.from(section().querySelectorAll("product-info")),
    );

    const variants = createMemo(() => {
      const variants = productInfoElements().map((productInfo) => {
        const [state] = getProductContext(productInfo);
        return state.product.selected_or_first_available_variant;
      });

      return uniqBy(variants, "id");
    });

    const variantsInCart = createMemo(() => {
      const cart = StorefrontCart.cart();
      if (!cart) return [];
      return variants().reduce((variants, variant) => {
        const variantInCart = cart.items.find(
          (item) => `${item.variant_id}` === `${variant.id}`,
        );
        if (variantInCart)
          variants.push({
            ...variant,
            quantity: variantInCart.quantity,
            selected_selling_plan: variant.selling_plan_allocations.find(
              (alloc) =>
                `${alloc.selling_plan_id}` ===
                `${variantInCart.properties._selling_plan}`,
            ),
          });
        return variants;
      }, []);
    });

    const actualPrice = createMemo(() => {
      const d = discount();
      const total = variantsInCart().reduce((total, variant) => {
        if (variant.selected_selling_plan)
          return total + variant.selected_selling_plan.price * variant.quantity;
        return total + variant.price * variant.quantity;
      }, 0);

      return getDiscountedPrice(total, d);
    });

    const compareAtPrice = createMemo(() => {
      const total = variantsInCart().reduce((total, variant) => {
        if (variant.selected_selling_plan)
          return (
            total +
            (variant.selected_selling_plan.compare_at_price || 0) *
              variant.quantity
          );
        return total + (variant.compare_at_price || 0) * variant.quantity;
      }, 0);

      return total;
    });

    const formattedActualPrice = () => formatValue("currency", actualPrice()),
      formattedCompareAtPrice = () => formatValue("currency", compareAtPrice()),
      priceClass = props.priceClass
        ? `variant-price ${props.priceClass}`
        : "variant-price",
      actualClass = () =>
        props.actualClass
          ? `variant-price__actual ${props.actualClass}`
          : "variant-price__actual",
      compareClass = () =>
        props.compareClass
          ? `variant-price__compare ${props.compareClass}`
          : "variant-price__compare";

    return html`
      <${Show}
        when=${() =>
          typeof actualPrice() === "number" &&
          typeof compareAtPrice() === "number"}
      >
        <${Price} class=${priceClass}>
          <${Show} when=${() => actualPrice() < compareAtPrice()}>
            <s class=${compareClass}>${formattedCompareAtPrice}</s>
          <//>
          <span class=${actualClass}>${formattedActualPrice}</span>
        <//>
      <//>
    `;
  },
);

customShadowlessElement(
  "bundle-one-time-price",
  {
    actualClass: "",
    priceClass: "",
    compareClass: "",
    sectionId: "",
  },
  (props, { element }) => {
    if (!props.sectionId)
      return console.warn(
        "bundle-subscription-price: section-id attribute required!",
      );

    const section = createMemo(() => document.getElementById(props.sectionId));
    const discount = getDiscount();

    const productInfoElements = createMemo(() =>
      Array.from(section().querySelectorAll("product-info")),
    );

    const variants = createMemo(() => {
      const variants = productInfoElements().map((productInfo) => {
        const [state] = getProductContext(productInfo);
        return state.product.selected_or_first_available_variant;
      });

      return uniqBy(variants, "id");
    });

    const variantsInCart = createMemo(() => {
      const cart = StorefrontCart.cart();
      if (!cart) return [];
      return variants().reduce((variants, variant) => {
        const variantInCart = cart.items.find(
          (item) => `${item.variant_id}` === `${variant.id}`,
        );
        if (variantInCart)
          variants.push({
            ...variant,
            quantity: variantInCart.quantity,
            selected_selling_plan: variant.selling_plan_allocations.find(
              (alloc) =>
                `${alloc.selling_plan_id}` ===
                `${variantInCart.properties._selling_plan}`,
            ),
          });
        return variants;
      }, []);
    });

    const actualPrice = createMemo(() => {
      const d = discount();
      const total = variantsInCart().reduce((total, variant) => {
        return total + variant.price * variant.quantity;
      }, 0);
      return getDiscountedPrice(total, d);
    });

    const compareAtPrice = createMemo(() => {
      return variantsInCart().reduce((total, variant) => {
        return total + variant.price * variant.quantity;
      }, 0);
    });

    const formattedActualPrice = () => formatValue("currency", actualPrice()),
      formattedCompareAtPrice = () => formatValue("currency", compareAtPrice()),
      priceClass = props.priceClass
        ? `variant-price ${props.priceClass}`
        : "variant-price",
      actualClass = () =>
        props.actualClass
          ? `variant-price__actual ${props.actualClass}`
          : "variant-price__actual",
      compareClass = () =>
        props.compareClass
          ? `variant-price__compare ${props.compareClass}`
          : "variant-price__compare";

    return html`
      <${Show}
        when=${() =>
          typeof actualPrice() === "number" &&
          typeof compareAtPrice() === "number"}
      >
        <${Price} class=${priceClass}>
          <${Show} when=${() => actualPrice() < compareAtPrice()}>
            <s class=${compareClass}>${formattedCompareAtPrice}</s>
          <//>
          <span class=${actualClass}>${formattedActualPrice}</span>
        <//>
      <//>
    `;
  },
);

customShadowlessElement("button-requirements", {}, (props, { element }) => {
  const button = element.querySelector("button"),
    requirements = Array.from(element.querySelectorAll("select")),
    controller = new AbortController();
  if (!button)
    return console.warn("button-requirements: button element not found!");
  if (!requirements.length) {
    button.removeAttribute("disabled");
    return console.warn("button-requirements: select elements not found!");
  }

  function someAreOptionsDisabled(selects) {
    return selects.some(checkIfSelectedOptionIsDisabled);
  }

  function checkIfSelectedOptionIsDisabled(select) {
    const option = select.selectedOptions.item(0);
    if (!option) return false;
    return option.disabled;
  }

  const [disabled, setDisabled] = createSignal(
    someAreOptionsDisabled(requirements),
  );

  createEffect(() => {
    const d = disabled();
    if (d) return button.setAttribute("disabled", "disabled");
    return button.removeAttribute("disabled");
  });

  element.addEventListener(
    "change",
    (event) => {
      if (event.target.tagName !== "SELECT") return;
      setDisabled(someAreOptionsDisabled(requirements));
    },
    { signal: controller.signal },
  );

  onCleanup(() => controller.abort("Component unmounted!"));
});

customShadowlessElement(
  "bundle-variant-price-total",
  { sellingPlan: 0, actualClass: "", priceClass: "", compareClass: "" },
  (props, { element }) => {
    const discount = getDiscount();

    const productInfo = element.closest("product-info");
    if (!productInfo)
      return console.warn(
        "bundle-variant-subscription-price: No product-info parent element found",
      );
    if (!props.sellingPlan)
      return console.warn(
        "bundle-variant-price: selling-plan attribute required!",
      );

    function getQuantity(select) {
      if (!select)
        select = productInfo.querySelector("select[name='quantity']");
      if (!select) return 1;
      const option = select.selectedOptions.item(0);
      if (option.disabled) return 1;
      return parseInt(option.value, 10);
    }

    const [quantity, setQuantity] = createSignal(getQuantity());
    const [state, _] = getProductContext(productInfo);
    const controller = new AbortController();

    const selectedVariant = createMemo(
      () => state.product.selected_or_first_available_variant,
    );

    const sellingPlanAllocation = createMemo(() => {
      return selectedVariant().selling_plan_allocations.find(
        (allocation) => allocation.selling_plan_id === props.sellingPlan,
      );
    });

    const purchaseOption = createMemo(
      () =>
        StorefrontCart.cart()?.attributes?.find(
          (attr) => attr.key === "_purchaseOption",
        )?.value || null,
    );

    const actualPrice = createMemo(() => {
      const d = discount();
      const variant = selectedVariant();
      const allocation = sellingPlanAllocation();
      const option = purchaseOption();
      const q = quantity();
      if (!variant) return null;
      if (allocation && option === "sub")
        return getDiscountedPrice(allocation.price, d) * q;
      return getDiscountedPrice(variant.price, d) * q;
    });

    const compareAtPrice = createMemo(() => {
      const variant = selectedVariant();
      const allocation = sellingPlanAllocation();
      const option = purchaseOption();
      const q = quantity();
      if (!variant) return null;
      if (allocation && option === "sub")
        return (allocation.compare_at_price || 0) * q;
      return (variant.compare_at_price || variant.price || 0) * q;
    });

    const formattedActualPrice = () => formatValue("currency", actualPrice()),
      formattedCompareAtPrice = () => formatValue("currency", compareAtPrice()),
      priceClass = () =>
        props.priceClass
          ? `variant-price ${props.priceClass}`
          : "variant-price",
      actualClass = () =>
        props.actualClass
          ? `variant-price__actual ${props.actualClass}`
          : "variant-price__actual",
      compareClass = () =>
        props.compareClass
          ? `variant-price__compare ${props.compareClass}`
          : "variant-price__compare";

    const clonedChildren = () =>
      Array.from(element.children).map((child) => child.cloneNode(true));

    element.replaceChildren(...[]);

    productInfo.addEventListener("change", (event) => {
      if (event.target.tagName !== "SELECT") return;
      setQuantity(getQuantity(event.target));
    });

    onCleanup(() => controller.abort("Component unmounted!"));

    createEffect(() => {
      console.log(actualPrice(), compareAtPrice());
    });

    return html`
      <${Show}
        when=${() =>
          typeof actualPrice() === "number" &&
          typeof compareAtPrice() === "number"}
        fallback=${clonedChildren}
      >
        <${Price} class=${priceClass}>
          <${Show} when=${() => actualPrice() < compareAtPrice()}>
            <s class=${compareClass}>${formattedCompareAtPrice}</s>
          <//>
          <span class=${actualClass}>${formattedActualPrice}</span>
        <//>
      <//>
    `;
  },
);
